
import {
  getUserDetails,
  gitAuthorization,
  GitTokenDetails
} from '@/gitIntegration/gitAuth';
import {
  createGitRepo,
  GitWorkspaceDetails,
  listGitRepos
} from '@/gitIntegration/gitRepo';
import { defineComponent } from 'vue';
import TextBubble from '@/modules/globalModule/components/TextBubble.vue';
import SearchableDropDown from '@/modules/globalModule/components/SearchableDropDown.vue';
import { toGitRepoName } from '@/modules/globalModule/components/helperMethods';
import { useGlobalStore } from '@/modules/globalModule/store';
import {
  getGitWorkspaceDetails,
  saveGitWorkspaceDetails
} from '@/firebase/databaseDependencyInjectables';
import { workspaceToFileTree } from '@/codeGeneration/codeGen';
import { gitCommit } from '@/gitIntegration/gitCommit';
import { gitPull } from '@/gitIntegration/gitPull';
import {
  createNewBranch,
  getAllGitBranches,
  GitBranch
} from '@/gitIntegration/gitBranch';

interface GitWs {
  id: number;
  name: string;
  owner?: string; // username of repo owner
}

export default defineComponent({
  name: 'GitMenu',
  components: {
    TextBubble,
    SearchableDropDown
  },
  data(): {
    gitAuthDetails: GitTokenDetails | undefined;
    gitWsDetails: GitWorkspaceDetails | undefined; // actual workspace chosen
    gitRepos: Array<GitWs>;
    newRepoName: string;
    selectedRepo: undefined | GitWs; // repo selected in the choose repo drop down
    workspaceId: string;
    gitBranchName: string;
    commitMessage: string;
    changingBranch: boolean;
    newBranchName: string;
    selectedBranch: GitBranch | undefined;
    gitBranches: GitBranch[];
    commitText: string;
    commitError: boolean;
  } {
    return {
      gitAuthDetails: undefined,
      gitWsDetails: undefined,
      selectedRepo: undefined,
      gitRepos: [],
      newRepoName: '',
      workspaceId: '',
      gitBranchName: 'main',
      commitMessage: '',
      changingBranch: false,
      newBranchName: '',
      gitBranches: [],
      selectedBranch: undefined,
      commitText: '',
      commitError: false
    };
  },
  async mounted() {
    this.commitText = '';
    this.workspaceId = this.$route.params.workspaceId as string;
    const gitAuthCode = await gitAuthorization(this.workspaceId);
    if (!gitAuthCode || !gitAuthCode.access_token) {
      this.$router.push(`/controlCenter/${this.workspaceId}`);
      return;
    }
    const repos = await listGitRepos(gitAuthCode.access_token);
    this.gitAuthDetails = gitAuthCode;
    this.gitRepos = repos.data.map((repo) => {
      return {
        id: repo.id,
        name: repo.name,
        owner: repo.owner.login
      };
    });
    this.gitWsDetails = getGitWorkspaceDetails(this.workspaceId);
    // getting selected branch details
    this.loadRepoBranches();
    if (this.gitWsDetails && this.gitWsDetails.branch)
      this.gitBranchName = this.gitWsDetails.branch;
  },
  methods: {
    async changeCurrentRepo(newRepo: GitWorkspaceDetails) {
      this.gitWsDetails = newRepo;
      if (!this.gitWsDetails.branch) this.gitWsDetails.branch = 'main';
      this.gitBranchName = this.gitWsDetails.branch;
      await this.loadRepoBranches();
    },
    async createNewRepo() {
      if (!this.gitAuthDetails || this.newRepoName.trim() === '') {
        return;
      }
      const globalStore = useGlobalStore();
      const repoDetails = await createGitRepo(
        this.gitAuthDetails.access_token,
        toGitRepoName(this.newRepoName),
        globalStore.workspaceName
      );
      await this.changeCurrentRepo({
        repoId: repoDetails.data.id,
        repoName: repoDetails.data.name
      });
      saveGitWorkspaceDetails(this.workspaceId, this.gitWsDetails!);
    },
    async viewGitRepo() {
      if (!this.gitAuthDetails || !this.gitWsDetails) return;
      const userDetails = await getUserDetails(
        this.gitAuthDetails?.access_token
      );
      window.open(
        `https://github.com/${
          this.gitWsDetails.repoOwner || userDetails.login
        }/${this.gitWsDetails.repoName}`,
        '_blank'
      );
    },
    async loadRepoBranches() {
      if (!this.gitAuthDetails || !this.gitWsDetails) return;
      this.gitBranches = await getAllGitBranches(
        this.gitAuthDetails.access_token,
        this.gitWsDetails.repoName
      );
    },
    async chooseExistingWorkspace() {
      if (!this.selectedRepo) return;
      await this.changeCurrentRepo({
        repoId: this.selectedRepo.id,
        repoName: this.selectedRepo.name,
        repoOwner: this.selectedRepo.owner
      });
      saveGitWorkspaceDetails(this.workspaceId, this.gitWsDetails!);
    },
    selectDifferentRepo() {
      this.gitWsDetails = undefined;
    },
    async commitAndPush() {
      if (!this.workspaceId) return;
      const fileTree = workspaceToFileTree(this.workspaceId);
      if (!this.gitAuthDetails || !this.gitWsDetails || !fileTree) return;
      try {
        const commitRes = await gitCommit(
          this.gitAuthDetails.access_token,
          fileTree,
          this.gitWsDetails.repoName,
          this.commitMessage,
          this.gitBranchName
        );
        this.commitError = false;
        this.commitText = `Success! Commit "${this.commitMessage}" successfully pushed to branch ${this.gitBranchName}.`;
      } catch (e: any) {
        this.commitError = true;
        if ((e.toString() as string).includes('Invalid tree info'))
          this.commitText =
            'Commit Failed: Committed with an empty tree. Try adding some packages and nodes before committing again.';
        if ((e.toString() as string).includes('Update is not a fast forward'))
          this.commitText = 'Commit Failed: No changes to commit.';
        else this.commitText = 'Commit Failed.';
      }
      this.loadRepoBranches();
    },
    async pullFromOrigin() {
      if (!this.gitAuthDetails || !this.gitWsDetails) return;
      try {
        await gitPull(
          this.gitAuthDetails.access_token,
          this.gitWsDetails.repoName,
          this.workspaceId
        );
        this.commitError = false;
        this.commitText = 'Pull successful';
      } catch (e) {
        console.warn(e);
        this.commitError = true;
        this.commitText = 'Pull failed';
      }
    },
    async createNewBranch() {
      if (
        this.newBranchName === '' ||
        !this.gitAuthDetails ||
        !this.gitWsDetails
      )
        return;
      await createNewBranch(
        this.gitAuthDetails.access_token,
        this.gitWsDetails.repoName,
        this.newBranchName,
        this.gitBranchName
      );
      this.gitBranchName = this.newBranchName;
      this.gitWsDetails.branch = this.gitBranchName;
      saveGitWorkspaceDetails(this.workspaceId, this.gitWsDetails);
      this.changingBranch = false;
      this.loadRepoBranches();
    },
    chooseSelectedBranch() {
      if (!this.selectedBranch) return;
      this.gitBranchName = this.selectedBranch.name;
      if (this.gitWsDetails) {
        this.gitWsDetails.branch = this.gitBranchName;
        saveGitWorkspaceDetails(this.workspaceId, this.gitWsDetails);
      }
      this.changingBranch = false;
    }
  }
});
